<template>
  <div class="limites-page">

    <!-- ══ HEADER ══ -->
    <div class="page-header mb-4">
      <div class="d-flex align-center">
        <div class="header-icon-pill mr-3">
          <v-icon color="white" size="22">mdi-shield-account</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-0">Límites de Vendedores</h1>
          <p class="page-subtitle mb-0">Controla el tope máximo de venta por vendedor</p>
        </div>
        <v-spacer />
        <v-btn color="primary" class="rounded-xl font-weight-bold px-5"
          elevation="0" @click="refresh" :loading="loading">
          <v-icon left size="18">mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>
    </div>

    <!-- ══ INFO BANNER ══ -->
    <v-alert type="info" dense outlined class="rounded-xl mb-4 text-body-2">
      Los vendedores <strong>sin límite</strong> pueden vender sin restricción.
      El sistema bloquea la venta cuando: <em>Saldo + Flotante + Tiquete &gt; Límite</em>.
    </v-alert>

    <!-- ══ TARJETAS RESUMEN (una por vendedor con límite) ══ -->
    <v-row v-if="vendedoresConLimite.length" class="mb-4">
      <v-col v-for="v in vendedoresConLimite" :key="v.cedula" cols="12" sm="6" md="4" lg="3">
        <v-card class="stat-card rounded-xl pa-4" elevation="0">
          <div class="d-flex align-center mb-3">
            <div class="avatar-pill mr-2">{{ initials(v.nombre) }}</div>
            <div class="stat-nombre text-truncate">{{ v.nombre }}</div>
          </div>
          <div class="stat-monto">₡{{ formatMonto(v.limite) }}</div>
          <div class="stat-label">Límite asignado</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ══ TABLA COMPLETA ══ -->
    <v-card class="glass-card rounded-xl overflow-hidden" elevation="0">
      <!-- toolbar interno -->
      <div class="d-flex align-center px-5 pt-5 pb-3 flex-wrap" style="gap:12px;">
        <div class="section-icon-pill mr-2">
          <v-icon color="white" size="14">mdi-account-cash</v-icon>
        </div>
        <span class="section-title">Todos los Vendedores</span>
        <v-spacer />
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Buscar..."
          outlined dense rounded hide-details
          class="modern-input" style="max-width:260px;"
        />
      </div>
      <v-divider style="opacity:0.08;" class="mx-5" />

      <v-data-table
        :headers="headers"
        :items="mergedItems"
        :search="search"
        :loading="loading"
        loading-text="Cargando vendedores..."
        no-data-text="No hay vendedores en esta compañía"
        class="limites-table"
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- Vendedor -->
        <template v-slot:[`item.nombre`]="{ item }">
          <div class="d-flex align-center" style="gap:10px;">
            <div class="avatar-pill">{{ initials(item.nombre) }}</div>
            <div>
              <div class="font-weight-bold" style="font-size:0.92rem;">{{ item.nombre }}</div>
              <div class="text-caption" style="opacity:0.55;">{{ item.cedula }}</div>
            </div>
          </div>
        </template>

        <!-- Límite actual — número grande y legible -->
        <template v-slot:[`item.limite`]="{ item }">
          <div v-if="item.limite > 0" class="limite-badge active">
            <v-icon size="14" class="mr-1" color="white">mdi-check-circle</v-icon>
            ₡{{ formatMonto(item.limite) }}
          </div>
          <div v-else class="limite-badge inactive">Sin límite</div>
        </template>

        <!-- Nuevo límite: input grande + botón -->
        <template v-slot:[`item.editar`]="{ item }">
          <div class="d-flex align-center" style="gap:8px; min-width:320px;">
            <v-text-field
              v-model.number="item.nuevoLimite"
              type="number" min="0" step="5000"
              outlined dense hide-details
              class="modern-input limite-input"
              style="max-width:200px;"
              :placeholder="item.limite > 0 ? formatMonto(item.limite) : '0'"
            >
              <template v-slot:prepend-inner>
                <span class="prefix-colones">₡</span>
              </template>
            </v-text-field>
            <v-btn
              color="success" rounded elevation="0"
              class="font-weight-bold px-5"
              style="height:40px; font-size:0.82rem;"
              :loading="item.saving"
              :disabled="item.nuevoLimite === null || item.nuevoLimite === ''"
              @click="guardarLimite(item)"
            >
              <v-icon size="16" left>mdi-content-save</v-icon>
              Guardar
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <Snackbar :text="snackText" :snackbar="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "LimitesVendedor",
  components: { Snackbar },

  data() {
    return {
      loading: false,
      search: "",
      usuarios: [],
      limitesActuales: [],
      snackText: "",
      snackValue: false,
      headers: [
        { text: "Vendedor",       value: "nombre",  sortable: true,  width: "25%" },
        { text: "Límite Actual",  value: "limite",  sortable: true,  width: "20%" },
        { text: "Nuevo Límite",   value: "editar",  sortable: false, width: "55%" },
      ],
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),

    mergedItems() {
      return this.usuarios
        .filter((u) => u.cedula && u.cedula !== "0") // excluir usuario "Todos"
        .map((u) => {
        const limReg = this.limitesActuales.find((l) => l.idUsuario === u.cedula);
        return {
          cedula: u.cedula,
          nombre: u.nombre || u.cedula,
          limite: limReg ? limReg.limite : 0,
          nuevoLimite: null,
          saving: false,
        };
      });
    },

    vendedoresConLimite() {
      return this.mergedItems.filter((v) => v.limite > 0);
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    async refresh() {
      this.loading = true;
      try {
        await Promise.all([this.getUsuarios(), this.getLimites()]);
      } finally {
        this.loading = false;
      }
    },

    async getUsuarios() {
      const { data } = await axios.get(
        `api/Usuarios/getUsuariosPorCia?companyId=${this.strCia}`
      );
      this.usuarios = data;
    },

    async getLimites() {
      try {
        const { data } = await axios.get(
          `api/Tiempos/GetAllLimitesVendedor?companyId=${this.strCia}`
        );
        this.limitesActuales = data;
      } catch {
        this.limitesActuales = [];
      }
    },

    async guardarLimite(item) {
      if (item.nuevoLimite === null || item.nuevoLimite === "") return;
      item.saving = true;
      try {
        await axios.post("api/Tiempos/SetLimiteVendedor", {
          companyId: this.strCia,
          idUsuario: item.cedula,
          limite: Number(item.nuevoLimite),
        });
        this.snackText = `✅ Límite de ${item.nombre} actualizado a ₡${this.formatMonto(item.nuevoLimite)}`;
        this.snackValue = true;
        item.nuevoLimite = null;
        await this.getLimites();
      } catch {
        this.snackText = "❌ Error al guardar el límite";
        this.snackValue = true;
      } finally {
        item.saving = false;
      }
    },

    initials(nombre) {
      if (!nombre) return "?";
      return nombre.split(" ").map((p) => p[0]).join("").substring(0, 2).toUpperCase();
    },

    formatMonto(val) {
      if (!val && val !== 0) return "0";
      return Number(val).toLocaleString("es-CR");
    },
  },
};
</script>

<style scoped>
/* ── Layout: ocupa todo el espacio disponible ── */
.limites-page {
  padding: 20px 24px;
  width: 100%;
  box-sizing: border-box;
}

/* ── Header ── */
.page-header { animation: fadeInDown 0.4s ease forwards; }
.header-icon-pill {
  width: 46px; height: 46px; border-radius: 14px;
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.35);
  flex-shrink: 0;
}
.page-title   { font-size: 1.3rem; font-weight: 800; letter-spacing: -0.3px; }
.page-subtitle{ font-size: 0.8rem; color: #64748b; }
.theme--dark .page-subtitle { color: #94a3b8; }

/* ── Tarjetas stat (vendedores con límite) ── */
.stat-card {
  background: linear-gradient(135deg, rgba(25,118,210,0.12), rgba(66,165,245,0.06)) !important;
  border: 1px solid rgba(25, 118, 210, 0.2) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(25,118,210,0.15) !important;
}
.theme--dark .stat-card {
  background: rgba(25, 118, 210, 0.1) !important;
  border-color: rgba(25, 118, 210, 0.25) !important;
}
.stat-nombre  { font-size: 0.88rem; font-weight: 700; max-width: 140px; }
.stat-monto   { font-size: 1.6rem; font-weight: 900; color: #1976D2; letter-spacing: -0.5px; line-height: 1.1; }
.theme--dark .stat-monto { color: #64B5F6; }
.stat-label   { font-size: 0.72rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }

/* ── Glass card ── */
.glass-card {
  background: rgba(255,255,255,0.75) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255,255,255,0.5) !important;
}
.theme--dark .glass-card {
  background: rgba(30,30,46,0.75) !important;
  border: 1px solid rgba(255,255,255,0.08) !important;
}

/* ── Section pill ── */
.section-icon-pill {
  width: 26px; height: 26px; border-radius: 8px;
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.section-title { font-size: 0.9rem; font-weight: 700; }

/* ── Tabla ── */
.limites-table >>> .v-data-table__wrapper { overflow-x: auto; }
.limites-table >>> tbody tr { transition: background 0.15s ease; }
.limites-table >>> tbody tr:hover { background: rgba(25,118,210,0.04) !important; }
.limites-table >>> th {
  font-size: 0.72rem !important; font-weight: 700 !important;
  letter-spacing: 0.6px !important; text-transform: uppercase;
  color: #64748b !important; border-bottom: 1px solid rgba(0,0,0,0.06) !important;
  padding-top: 14px !important; padding-bottom: 14px !important;
}
.limites-table >>> td { padding-top: 12px !important; padding-bottom: 12px !important; }
.theme--dark .limites-table >>> th { color: #94a3b8 !important; }

/* ── Badge de límite — grande y claro ── */
.limite-badge {
  display: inline-flex; align-items: center;
  padding: 6px 14px; border-radius: 20px;
  font-size: 1rem; font-weight: 800; letter-spacing: -0.3px;
  white-space: nowrap;
}
.limite-badge.active {
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  color: white;
  box-shadow: 0 3px 10px rgba(25,118,210,0.3);
}
.limite-badge.inactive {
  background: rgba(100,116,139,0.12);
  color: #94a3b8;
}

/* ── Input límite ── */
.limite-input >>> .v-input__slot {
  background: rgba(255,255,255,0.6) !important;
  min-height: 40px !important;
  font-size: 1rem !important;
  font-weight: 700 !important;
}
.theme--dark .limite-input >>> .v-input__slot {
  background: rgba(0,0,0,0.2) !important;
}
.limite-input >>> input {
  font-size: 1rem !important;
  font-weight: 700 !important;
}
.prefix-colones {
  font-size: 1rem; font-weight: 700; color: #1976D2;
  margin-right: 2px; line-height: 1;
}
.theme--dark .prefix-colones { color: #64B5F6; }

/* ── Avatar ── */
.avatar-pill {
  width: 36px; height: 36px; border-radius: 10px;
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem; font-weight: 800; flex-shrink: 0;
}

/* ── Search input ── */
.modern-input >>> .v-input__slot {
  background: rgba(255,255,255,0.6) !important; transition: all 0.2s ease;
}
.modern-input >>> .v-input__slot:hover { background: rgba(255,255,255,0.95) !important; }
.theme--dark .modern-input >>> .v-input__slot { background: rgba(0,0,0,0.2) !important; }

@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
